import { keepPreviousData, useQuery, type UseQueryResult } from '@tanstack/react-query';
import type { ApiError } from '@app/api/ApiError';
import { get, withPath } from '@app/api/typed';
import {
  forQuery,
  type AuditEntry,
  type AuditEntryDetail,
  type AuditFilterValues,
  type AuditPage,
} from './AuditModel';

/**
 * Hooks de T23 (FR-113). Só leitura: a auditoria não se edita nem se exclui, e
 * todos os papéis a consultam (13.8).
 */
export const auditKeys = {
  todas: ['audit'] as const,
  lista: (filters: AuditFilterValues) => ['audit', 'list', forQuery(filters)] as const,
  detalhe: (id: string) => ['audit', 'detalhe', id] as const,
};

export function useAudit(filters: AuditFilterValues): UseQueryResult<AuditPage, ApiError> {
  return useQuery<AuditPage, ApiError>({
    queryKey: auditKeys.lista(filters),
    queryFn: async () => {
      const page = await get('/api/v1/audit', { params: forQuery(filters) });
      return { ...page, itens: (page.items ?? []) as AuditEntry[] };
    },
    placeholderData: keepPreviousData,
  });
}

/** Detalhe do registro — antes/depois, envio, resposta e `request_id` (FR-111). */
export function useAuditEntry(id: string | null): UseQueryResult<AuditEntryDetail, ApiError> {
  return useQuery<AuditEntryDetail, ApiError>({
    queryKey: auditKeys.detalhe(id ?? ''),
    queryFn: () =>
      get(withPath('/api/v1/audit/{id}', { id: id as string }) as '/api/v1/audit/{id}'),
    enabled: Boolean(id),
  });
}
